import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { ColorSchemeScript, MantineProvider, Stack, Text } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import React from 'react';
import AuthProvider from 'react-auth-kit';
import createStore from 'react-auth-kit/createStore';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { theme } from './theme';

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { modals, ModalsProvider } from '@mantine/modals';
import '@mantine/notifications/styles.css';
import { withScalars } from 'apollo-link-scalars';
import dayjs from 'dayjs';
import 'dayjs/locale/sk';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'mantine-datatable/styles.css';
import '@mantine/charts/styles.css';

import { DatesProvider } from '@mantine/dates';
import { buildClientSchema, IntrospectionQuery } from 'graphql';
import { DateTimeResolver } from 'graphql-scalars';
import introspectionResult from './__generated__/graphql.schema.json';
import { router } from './router';

dayjs.extend(customParseFormat);

const store = createStore({
  authName: 'token',
  authType: 'cookie',
  cookieDomain: window.location.hostname,
  cookieSecure: window.location.protocol === 'https:',
  debug: !import.meta.env.PROD,
});

const scalarsLink = withScalars({
  schema: buildClientSchema(introspectionResult as unknown as IntrospectionQuery),
  typesMap: {
    DateTime: DateTimeResolver,
  },
});

const httpLink = createHttpLink({
  uri: import.meta.env.PROD
    ? 'https://api.laskykrasky.sk/graphql'
    : 'http://localhost:3000/graphql',
  credentials: 'include',
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.PROD ? 'wss://api.laskykrasky.sk/graphql' : 'ws://localhost:3000/graphql',
    connectionParams: () => {
      const token = localStorage.getItem('token');
      return token ? { authorization: `Bearer ${token}` } : {};
    },
  })
);

// The split function takes three parameters:
//
// * A function that's called for each operation to execute
// * The Link to use for an operation if the function returns a "truthy" value
// * The Link to use for an operation if the function returns a "falsy" value
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, path, extensions, locations }) => {
      modals.open({
        title: `Vyskytla sa chyba`,
        color: 'red',
        children: (
          <Stack>
            <Text c="red">{message}</Text>
            {path && <Text>{JSON.stringify(path, null, 2)}</Text>}
            {extensions && <Text>{JSON.stringify(extensions, null, 2)}</Text>}
            {locations && <Text>{JSON.stringify(locations, null, 2)}</Text>}
          </Stack>
        ),
        size: 'auto',
      });
    });
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([errorLink, scalarsLink, splitLink]),
  defaultOptions: {
    query: { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
    mutate: { awaitRefetchQueries: true, fetchPolicy: 'network-only', refetchQueries: 'active' },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ColorSchemeScript defaultColorScheme="dark" />
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <DatesProvider settings={{ locale: 'sk' }}>
        <ApolloProvider client={client}>
          <AuthProvider store={store}>
            <Notifications />
            <ModalsProvider />
            <RouterProvider router={router} />
          </AuthProvider>
        </ApolloProvider>
      </DatesProvider>
    </MantineProvider>
  </React.StrictMode>
);
